// Mirrors afto_api's lib/constants.js ACCOUNT_STATUSES — keep both in sync.
// One lifecycle field is what every surface reads to decide whether an account
// is live; it replaced inferring that from a Stripe status plus a config row.
export const ACCOUNT_STATUSES = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  PAST_DUE: 'past_due',
  CANCELING: 'canceling',
  PAUSED: 'paused',
  CLOSING: 'closing',
  CLOSED: 'closed',
};

// Tone drives the banner's colour and icon. Deliberately not the only signal —
// every tone also carries its own wording and icon, so colour is never the sole
// indicator of state.
export const ACCOUNT_STATUS_TONES = {
  NEUTRAL: 'neutral',
  POSITIVE: 'positive',
  WARNING: 'warning',
  CRITICAL: 'critical',
};

// What the merchant is told, and the single action that resolves it. One CTA
// per state on purpose — a banner offering two equal choices is a banner the
// merchant ignores.
export const ACCOUNT_STATUS_PRESENTATION = {
  [ACCOUNT_STATUSES.TRIALING]: {
    tone: ACCOUNT_STATUS_TONES.NEUTRAL,
    title: 'Free trial',
    actionLabel: 'Choose a plan',
  },
  [ACCOUNT_STATUSES.ACTIVE]: {
    tone: ACCOUNT_STATUS_TONES.POSITIVE,
    title: 'Your account is active',
    actionLabel: null,
  },
  // Says what stopped and what did not. A merchant reading this has just found
  // their store closed, and the first question is whether their work survived.
  [ACCOUNT_STATUSES.EXPIRED]: {
    tone: ACCOUNT_STATUS_TONES.CRITICAL,
    title: 'Your trial has ended',
    description:
      'Your store has stopped accepting new orders. Your products, settings and customers are all still here — choose a plan to reopen.',
    actionLabel: 'Choose a plan',
  },
  [ACCOUNT_STATUSES.PAST_DUE]: {
    tone: ACCOUNT_STATUS_TONES.CRITICAL,
    title: 'Payment failed',
    description:
      'Update your card to keep your store online. We will retry automatically in the meantime.',
    actionLabel: 'Update payment method',
  },
  [ACCOUNT_STATUSES.CANCELING]: {
    tone: ACCOUNT_STATUS_TONES.WARNING,
    title: 'Your plan is ending',
    actionLabel: 'Keep my plan',
  },
  [ACCOUNT_STATUSES.PAUSED]: {
    tone: ACCOUNT_STATUS_TONES.WARNING,
    title: 'Account paused',
    description:
      'Your store is not accepting new orders. Your data and payment details are safe.',
    actionLabel: 'Resume account',
  },
  [ACCOUNT_STATUSES.CLOSING]: {
    tone: ACCOUNT_STATUS_TONES.CRITICAL,
    title: 'Account closing',
    actionLabel: 'Reactivate account',
  },
  [ACCOUNT_STATUSES.CLOSED]: {
    tone: ACCOUNT_STATUS_TONES.CRITICAL,
    title: 'Account closed',
    description: 'This account has been closed. Contact support if you need it restored.',
    actionLabel: null,
  },
};

// Statuses in which the storefront still takes orders — mirrors the API's
// ORDER_ACCEPTING_ACCOUNT_STATUSES so the UI's "your store is live" line can
// never disagree with what the checkout actually does.
export const ORDER_ACCEPTING_ACCOUNT_STATUSES = [
  ACCOUNT_STATUSES.TRIALING,
  ACCOUNT_STATUSES.ACTIVE,
  ACCOUNT_STATUSES.PAST_DUE,
  ACCOUNT_STATUSES.CANCELING,
];

export const isAcceptingOrders = (status) => ORDER_ACCEPTING_ACCOUNT_STATUSES.includes(status);

// Whole days from now until `date`, floored at 0. Used for the trial, pause and
// closing countdowns, which are all "how long do I have left" reads.
export const daysUntil = (date) => {
  if (!date) return null;

  const target = new Date(date).getTime();
  if (Number.isNaN(target)) return null;

  const DAY_MS = 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((target - Date.now()) / DAY_MS));
};
